import React, { useState, createContext, useContext } from 'react'
import { apiDelete, apiPost, apiGet, apiPatch } from '~/util/api'
import { useIsMobile, useIsDesktop } from '../../hooks/useMediaQuery'
import { useToast } from '../../lib/ui/toast'

const PracticeInterviewingContext = createContext()

export const usePracticeInterviewing = () => {
  return useContext(PracticeInterviewingContext)
}

const PracticeInterviewProvider = ({
  current_user_practice_interviews,
  current_user_resumes,
  practice_interview_images,
  practice_interview_ai_requests,
  current_user_saved_jobs,
  current_user,
  eu,
  children,
}) => {
  const isMobile = useIsMobile()
  const isDesktop = useIsDesktop()
  const [practiceInterviews, setPracticeInterviews] = useState(
    current_user_practice_interviews,
  )
  const [practiceInterviewAiRequests, setPracticeInterviewAiRequests] =
    useState(practice_interview_ai_requests)
  const [resumes, setResumes] = useState(current_user_resumes)
  // eslint-disable-next-line no-unused-vars
  const [savedJobs, setSavedJobs] = useState(current_user_saved_jobs)
  const [showForm, setShowForm] = useState(false)
  const [formIndex, setFormIndex] = useState(0)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [showInterview, setShowInterview] = useState(false)
  const [interviewParams, setInterviewParams] = useState({})
  const [practiceMode, setPracticeMode] = useState(true)
  const [audioInterview, setAudioInterview] = useState(false)
  const [generatingInterview, setGeneratingInterview] = useState(false)
  const [interviewResponses, setInterviewResponses] = useState([])
  const [showResponses, setShowResponses] = useState(false)
  const [filenames, setFilenames] = useState([])
  const [activeTab, setActiveTab] = useState('howDoesPracticeInterviewing')
  const [disableContinue, setDisableContinue] = useState(true)
  const [documentId, setDocumentId] = useState(0)
  const { toast } = useToast()

  const deletePracticeInterview = async (interview) => {
    try {
      await apiDelete(`/interview/practice_interviewing/${interview?.id}`)
      setPracticeInterviews((prev) => prev.filter((ele) => ele !== interview))
    } catch (error) {
      console.log(error)
    }
    return
  }

  const generatePracticeInterview = async () => {
    setShowForm(false)
    setGeneratingInterview(true)

    const token = document.querySelector('meta[name="csrf-token"]').content
    const config = {
      headers: { 'content-type': 'multipart/form-data', 'X-CSRF-TOKEN': token },
    }
    let response
    try {
      response = await apiPost(
        `/interview/practice_interviewing/`,
        {
          number_of_questions: interviewParams['number_of_questions'] || 1,
          job_title: interviewParams['job_title'],
          job_description: interviewParams['job_description'],
          company: interviewParams['company'],
          industry: interviewParams['industry'],
          resume_id: interviewParams['resume_id'],
          resume_file: interviewParams['resume_file'],
          practice_mode: practiceMode,
          audio_interview: audioInterview,
        },
        config,
      )
      setGeneratingInterview(false)
      setShowInterview(true)
      setDocumentId(response.data.document_id)
      setInterviewResponses(response.data.responses)
      toast({ description: 'Practice interview generated successfully' })
    } catch (error) {
      console.log(error)
      setGeneratingInterview(false)
      setShowForm(true)
      toast({
        description: 'Error: Interview generation failed.',
        variant: 'destructive',
      })
    }
  }

  const getInterviewResponses = async (id) => {
    let response
    try {
      response = await apiGet(
        `/interview/practice_interviewing/${id}/finished_responses/`,
      )
      setInterviewResponses(response.data.responses)
      setFilenames(response.data.filenames)
      setPracticeInterviews(response.data.practice_interviews)
      setPracticeInterviewAiRequests(response.data.ai_requests)
    } catch (error) {
      console.log(error)
    }
  }

  const saveInterviewResponse = async (document_id) => {
    await getInterviewResponses(document_id)
    if (questionIndex < interviewResponses.length - 1) {
      setQuestionIndex(questionIndex + 1)
    } else {
      setShowResponses(true)
      setShowInterview(false)
    }
  }

  const updateInterview = async (interview, interviewName) => {
    let response
    try {
      response = await apiPatch(
        `/interview/practice_interviewing/${interview.id}`,
        { name: interviewName },
      )
      setInterviewResponses(response.data.responses)
      setFilenames(response.data.filenames)
      setPracticeInterviews(response.data.practice_interviews)
      setPracticeInterviewAiRequests(response.data.ai_requests)
    } catch (error) {
      console.log(error)
    }
  }

  const value = {
    practiceInterviews,
    setPracticeInterviews,
    resumes,
    setResumes,
    practiceInterviewAiRequests,
    setPracticeInterviewAiRequests,
    practice_interview_images,
    savedJobs,
    deletePracticeInterview,
    showForm,
    setShowForm,
    formIndex,
    setFormIndex,
    questionIndex,
    setQuestionIndex,
    showInterview,
    setShowInterview,
    interviewParams,
    setInterviewParams,
    practiceMode,
    setPracticeMode,
    audioInterview,
    setAudioInterview,
    generatingInterview,
    setGeneratingInterview,
    generatePracticeInterview,
    interviewResponses,
    setInterviewResponses,
    showResponses,
    setShowResponses,
    getInterviewResponses,
    saveInterviewResponse,
    updateInterview,
    filenames,
    setFilenames,
    activeTab,
    setActiveTab,
    documentId,
    setDocumentId,
    disableContinue,
    setDisableContinue,
    current_user,
    eu,
    isMobile,
    isDesktop,
  }
  return (
    <PracticeInterviewingContext.Provider value={value}>
      {children}
    </PracticeInterviewingContext.Provider>
  )
}

export default PracticeInterviewProvider
